import { useState, useMemo } from 'react';
import type { RadioProgram } from '@/mocks/adminSchedule';
import { dayNames, dayShortNames, programTypeColors } from '@/mocks/adminSchedule';

interface MonthViewProps {
  programs: RadioProgram[];
  onEdit?: (program: RadioProgram) => void;
  onDelete?: (program: RadioProgram) => void;
  onDuplicate?: (program: RadioProgram) => void;
  onQuickTimeChange?: (program: RadioProgram) => void;
  onToggleActive?: (program: RadioProgram) => void;
  onCancel?: (program: RadioProgram) => void;
}

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export default function MonthView({ programs, onEdit, onDelete, onDuplicate, onQuickTimeChange, onToggleActive, onCancel }: MonthViewProps) {
  const now = new Date();
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [selectedProgram, setSelectedProgram] = useState<RadioProgram | null>(null);

  const goToPrevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear((y) => y - 1); }
    else setCurrentMonth((m) => m - 1);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear((y) => y + 1); }
    else setCurrentMonth((m) => m + 1);
  };

  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days: { day: number; date: Date; isCurrentMonth: boolean }[] = [];

    const prevMonthLast = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth - 1, prevMonthLast - i);
      days.push({ day: prevMonthLast - i, date: d, isCurrentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(currentYear, currentMonth, i);
      days.push({ day: i, date: d, isCurrentMonth: true });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(currentYear, currentMonth + 1, i);
      days.push({ day: i, date: d, isCurrentMonth: false });
    }

    return days;
  }, [currentYear, currentMonth]);

  const getProgramsForDate = (date: Date): RadioProgram[] => {
    const dayOfWeek = date.getDay();
    return programs.filter((p) => p.dayOfWeek === dayOfWeek);
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-5">
        <button onClick={goToPrevMonth} className="w-8 h-8 rounded-lg bg-background-50 border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-100 transition-colors">
          <i className="ri-arrow-left-s-line text-foreground-600"></i>
        </button>
        <h3 className="text-foreground-950 font-bold text-base">
          {monthNames[currentMonth]} {currentYear}
        </h3>
        <button onClick={goToNextMonth} className="w-8 h-8 rounded-lg bg-background-50 border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-100 transition-colors">
          <i className="ri-arrow-right-s-line text-foreground-600"></i>
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {[0, 1, 2, 3, 4, 5, 6].map((d) => (
          <div key={d} className="text-center py-1.5">
            <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">{dayShortNames[d]}</p>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map(({ day, date, isCurrentMonth }, idx) => {
          const dayPrograms = getProgramsForDate(date);
          const isTdy = isToday(date);

          return (
            <div
              key={idx}
              className={`min-h-[72px] rounded-lg p-1.5 transition-colors ${
                isCurrentMonth
                  ? isTdy
                    ? 'bg-primary-50/70 border border-primary-200'
                    : 'bg-background-50 border border-background-200/60'
                  : 'bg-background-100/40 border border-background-100'
              }`}
            >
              <p className={`text-[11px] font-bold mb-1 ${
                isTdy
                  ? 'text-primary-600'
                  : isCurrentMonth ? 'text-foreground-800' : 'text-foreground-300'
              }`}>
                {day}
              </p>
              <div className="space-y-0.5">
                {dayPrograms.slice(0, 3).map((p) => {
                  const colors = programTypeColors[p.type];
                  return (
                    <div
                      key={p.id}
                      onClick={() => setSelectedProgram(p)}
                      className={`px-1 py-0.5 rounded text-[8px] font-semibold truncate cursor-pointer ${colors.bg} ${colors.text}`}
                      title={`${p.name} · ${p.startTime}-${p.endTime}`}
                    >
                      {p.startTime.substring(0, 2)} {p.name.substring(0, 16)}{p.name.length > 16 ? '…' : ''}
                    </div>
                  );
                })}
                {dayPrograms.length > 3 && (
                  <p className="text-foreground-400 text-[8px] font-medium pl-1">
                    +{dayPrograms.length - 3} más
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Program preview */}
      {selectedProgram && (
        <div className="mt-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-background-200 overflow-hidden">
                <img src={selectedProgram.hostAvatar} alt="" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-foreground-950 font-semibold text-sm">{selectedProgram.name}</p>
                <p className="text-foreground-500 text-xs">{dayNames[selectedProgram.dayOfWeek]} · {selectedProgram.startTime} - {selectedProgram.endTime}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {onToggleActive && (
                <button onClick={() => onToggleActive(selectedProgram)} className={`w-6 h-6 rounded-md flex items-center justify-center cursor-pointer transition-colors ${
                  selectedProgram.isActive ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-foreground-100 hover:bg-foreground-200'
                }`} title={selectedProgram.isActive ? 'Desactivar' : 'Activar'}>
                  <i className={`${selectedProgram.isActive ? 'ri-toggle-line text-emerald-600' : 'ri-toggle-fill text-foreground-400'} text-xs`}></i>
                </button>
              )}
              {onQuickTimeChange && (
                <button onClick={() => onQuickTimeChange(selectedProgram)} className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-sky-50" title="Cambiar horario">
                  <i className="ri-time-line text-foreground-500 text-xs"></i>
                </button>
              )}
              {onCancel && !selectedProgram.cancelled && (
                <button onClick={() => onCancel(selectedProgram)} className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100" title="Cancelar programa">
                  <i className="ri-close-circle-line text-red-500 text-xs"></i>
                </button>
              )}
              {onDuplicate && (
                <button onClick={() => onDuplicate(selectedProgram)} className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-50" title="Duplicar">
                  <i className="ri-file-copy-line text-foreground-500 text-xs"></i>
                </button>
              )}
              {onEdit && (
                <button onClick={() => onEdit(selectedProgram)} className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200">
                  <i className="ri-pencil-line text-foreground-500 text-xs"></i>
                </button>
              )}
              {onDelete && (
                <button onClick={() => onDelete(selectedProgram)} className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100">
                  <i className="ri-delete-bin-line text-red-500 text-xs"></i>
                </button>
              )}
              <button onClick={() => setSelectedProgram(null)} className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200">
                <i className="ri-close-line text-foreground-500 text-xs"></i>
              </button>
            </div>
          </div>
          <p className="text-foreground-600 text-xs leading-relaxed">{selectedProgram.description}</p>
        </div>
      )}
    </div>
  );
}